import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  Mail,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Info,
} from "lucide-react";
import axios from "axios";
import { buildApiPath, API_ENDPOINTS, getApiUrl } from "../utils/api";

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError(t("auth.emailRequired"));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t("auth.invalidEmailFormat"));
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const handleConfirmSend = async () => {
    setIsLoading(true);
    setError("");
    setShowConfirmation(false);

    try {
      // Create axios instance without authentication
      const axiosInstance = axios.create({
        baseURL: getApiUrl(),
        timeout: 30000,
        headers: {
          "Content-Type": "application/json",
        },
      });

      await axiosInstance.post(
        buildApiPath(API_ENDPOINTS.AUTH.REQUEST_PASSWORD_RESET),
        {
          email: email.toLowerCase().trim(),
          language: i18n.language === "ja" ? "jp" : "en",
        },
      );

      setSuccess(true);
    } catch (err) {
      console.error("Password reset request error:", err);
      // Always show success to prevent email enumeration
      setSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess(false);
    setShowConfirmation(false);
    onClose();
  };

  const handleReset = () => {
    setSuccess(false);
    setEmail("");
    setError("");
    setShowConfirmation(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Confirmation Dialog */}
          {showConfirmation && !success && (
            <>
              <div className="mb-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                  <Info className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                  {t("auth.confirmPasswordReset")}
                </h2>
                <p className="text-gray-600 text-center">
                  {t("auth.confirmPasswordResetDescription", { email })}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p>{t("auth.passwordResetInfo")}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  {t("auth.cancel")}
                </button>
                <button
                  onClick={handleConfirmSend}
                  disabled={isLoading}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-xl text-white font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                      {t("auth.sending")}
                    </>
                  ) : (
                    t("auth.sendEmail")
                  )}
                </button>
              </div>
            </>
          )}

          {/* Initial Form */}
          {!success && !showConfirmation && (
            <>
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t("auth.forgotPassword")}
                </h2>
                <p className="text-gray-600">
                  {t("auth.forgotPasswordDescription")}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                      <span className="text-sm text-red-800">{error}</span>
                    </div>
                  </div>
                )}

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="reset-email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("auth.emailAddress")}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError("");
                      }}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder={t("auth.enterEmail")}
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    {t("auth.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-xl text-white font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {t("auth.continue")}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Success Message */}
          {success && (
            <div className="text-center py-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t("auth.checkYourEmail")}
              </h3>
              <p className="text-gray-600 mb-2">
                {t("auth.passwordResetEmailSent")}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t("auth.passwordResetEmailNote", { email })}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleClose}
                  className="w-full py-3 px-4 border border-transparent rounded-xl text-white font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                >
                  {t("auth.backToLogin")}
                </button>
                <button
                  onClick={handleReset}
                  className="w-full py-3 px-4 text-sm text-gray-600 hover:text-gray-900 transition-colors flex items-center justify-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("auth.tryAnotherEmail")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
